import { toDate } from "views/admin/default/dashboardData";

// Suivi de maintenance de chaque site, calculé une seule fois à partir des forfaits et des interventions déjà chargés.
// Fonction pure, pour pouvoir la tester

// `restant` : heures restantes d'un forfait horaire ; undefined pour un annuel
export type ForfaitDuSite = { forfait: ForfaitType; restant?: number };

export type SuiviSite = {
  // Tous les forfaits actifs du site : les horaires du plus urgent au moins urgent, puis les annuels
  forfaits: ForfaitDuSite[];
  // Forfait qui décide de l'ordre de la liste : le premier de `forfaits`
  forfait: ForfaitType | null;
  // Heures restantes de ce forfait
  restant?: number;
  derniereIntervention?: Date;
  derniersPlugins?: Date;
  derniereSauvegarde?: Date;
};

const plusRecente = (a: Date | undefined, b: Date) => (!a || b > a ? b : a);

function tempsConsomme(interventions: InterventionType[]) {
  const consomme = new Map<string, number>();
  interventions.forEach((intervention) => {
    consomme.set(intervention.idforfait, (consomme.get(intervention.idforfait) || 0) + (intervention.temps_passe || 0));
  });
  return consomme;
}

function forfaitsDuSite(idSite: string, forfaits: ForfaitType[], consomme: Map<string, number>): ForfaitDuSite[] {
  const duSite = forfaits
    .filter((forfait) => forfait.statut === true && forfait.idsites?.includes(idSite))
    .map((forfait) => ({
      forfait,
      restant: forfait.type === 1 ? (forfait.temps || 0) - (consomme.get(forfait.id) || 0) : undefined,
    }));
  return [
    ...duSite.filter((ligne) => ligne.restant !== undefined).sort((a, b) => a.restant - b.restant),
    ...duSite.filter((ligne) => ligne.restant === undefined),
  ];
}

export function suiviDesSites(
  sites: SiteType[],
  forfaits: ForfaitType[],
  interventions: InterventionType[]
): Map<string, SuiviSite> {
  const consomme = tempsConsomme(interventions);
  const suivi = new Map<string, SuiviSite>();
  sites.forEach((site) => {
    const duSite = forfaitsDuSite(site.id, forfaits, consomme);
    suivi.set(site.id, { forfaits: duSite, forfait: duSite[0]?.forfait ?? null, restant: duSite[0]?.restant });
  });

  // Seuls les sites cochés sur l'intervention comptent
  interventions.forEach((intervention) => {
    if (!intervention.date_intervention) {
      return;
    }
    const date = toDate(intervention.date_intervention);
    (intervention.idsites ?? []).forEach((id) => {
      const ligne = suivi.get(id);
      if (!ligne) {
        return;
      }
      ligne.derniereIntervention = plusRecente(ligne.derniereIntervention, date);
      if (intervention.plugins) {
        ligne.derniersPlugins = plusRecente(ligne.derniersPlugins, date);
      }
      if (intervention.sauvegarde) {
        ligne.derniereSauvegarde = plusRecente(ligne.derniereSauvegarde, date);
      }
    });
  });
  return suivi;
}

export type DetailSite = {
  forfaits: ForfaitDuSite[];
  interventions: InterventionType[];
};

// Détail d'un site : ses forfaits actifs et ses interventions les plus récentes
export function detailDuSite(idSite: string, forfaits: ForfaitType[], interventions: InterventionType[], nombre = 8): DetailSite {
  return {
    forfaits: forfaitsDuSite(idSite, forfaits, tempsConsomme(interventions)),
    interventions: interventions
      .filter((intervention) => intervention.idsites?.includes(idSite) && intervention.date_intervention)
      .sort((a, b) => toDate(b.date_intervention).getTime() - toDate(a.date_intervention).getTime())
      .slice(0, nombre),
  };
}

// Ordre de travail tant que rien n'est collecté : les mises à jour notées les plus anciennes d'abord,
// puis les sites sans forfait horaire, qui n'ont pas de suivi d'interventions
export function prioriteDuSite(suivi: SuiviSite) {
  return suivi.forfait?.type === 1 ? suivi.derniersPlugins?.getTime() ?? 0 : Number.MAX_SAFE_INTEGER;
}

// --- État collecté par n8n (`site_etat`) ---

// Au-delà, une donnée collectée est trop ancienne pour être affichée comme vérifiée : n8n ne publie peut-être plus
export const COLLECTE_MAX_HEURES = 3;
const HEURE = 3600 * 1000;
// Délai au-delà duquel une sauvegarde est en retard, selon sa fréquence
const RETARD_HEURES: Record<string, number> = { quotidien: 36, hebdo: 8 * 24, mensuel: 32 * 24 };
const FREQUENCES: Record<string, string> = {
  quotidien: "quotidien",
  quotidienne: "quotidien",
  hebdo: "hebdo",
  hebdomadaire: "hebdo",
  mensuel: "mensuel",
  mensuelle: "mensuel",
};

export type EtatSauvegarde =
  | "echec"
  | "echec_envoi"
  | "aucune"
  | "en_retard"
  | "avertissement"
  | "jamais"
  | "ok"
  | "autre"
  | "non_prevue"
  | "non_collecte";
export type EtatEnLigne = "en_ligne" | "contenu_anormal" | "hors_ligne" | "a_verifier" | "non_collecte";
export type EtatLigne = "a_traiter" | "a_surveiller" | "partiel" | "non_collecte" | "a_jour";

// Du plus grave au moins grave : un site à plusieurs tâches prend l'état de la pire
const GRAVITE: EtatSauvegarde[] = ["echec", "echec_envoi", "aucune", "en_retard", "avertissement", "jamais", "ok"];

export const tropAncien = (date: Date | null, maintenant: Date) =>
  !date || maintenant.getTime() - date.getTime() > COLLECTE_MAX_HEURES * HEURE;

export type ResultatSauvegarde = { etat: EtatSauvegarde; date?: Date; message?: string };

// État d'une tâche de sauvegarde : son dernier statut, puis son retard
export function etatDeLaTache(tache: TacheSauvegarde, maintenant: Date): ResultatSauvegarde {
  if (tache.statut === "echec_envoi") return { etat: "echec_envoi", date: tache.derniere_execution, message: tache.message };
  if (tache.statut === "echec") return { etat: "echec", date: tache.derniere_execution, message: tache.message };
  if (!tache.derniere_execution) return { etat: "jamais" };
  const limite = RETARD_HEURES[FREQUENCES[String(tache.frequence).toLowerCase().trim()]];
  if (limite && (!tache.dernier_succes || maintenant.getTime() - tache.dernier_succes.getTime() > limite * HEURE)) {
    return { etat: "en_retard", date: tache.dernier_succes ?? undefined };
  }
  if (tache.statut === "avertissement") return { etat: "avertissement", date: tache.derniere_execution, message: tache.message };
  return { etat: "ok", date: tache.dernier_succes ?? tache.derniere_execution };
}

// Sauvegarde d'un site : sa méthode s'il n'est pas sauvegardé par Akeeba, sinon la pire de ses tâches actives
export function sauvegardeDuSite(site: SiteType, etat: SiteEtatType | undefined, maintenant: Date): ResultatSauvegarde {
  if (site.sauvegarde === "autre") return { etat: "autre" };
  if (site.sauvegarde === "aucune") return { etat: "non_prevue" };
  if (!etat || tropAncien(etat.collecte, maintenant)) return { etat: "non_collecte" };
  const taches = etat.sauvegardes.filter((tache) => tache.actif).map((tache) => etatDeLaTache(tache, maintenant));
  if (!taches.length) return { etat: "aucune" };
  const pire = taches.reduce((a, b) => (GRAVITE.indexOf(b.etat) < GRAVITE.indexOf(a.etat) ? b : a));
  // Tout va bien : la date affichée est celle du dernier succès le plus récent
  if (pire.etat === "ok") {
    return { etat: "ok", date: taches.reduce((d, t) => (t.date && (!d || t.date > d) ? t.date : d), undefined as Date | undefined) };
  }
  return pire;
}

export type ResultatEnLigne = { etat: EtatEnLigne; depuis?: Date; message?: string };

// Disponibilité d'un site, si elle a été contrôlée récemment
export function enLigneDuSite(etat: SiteEtatType | undefined, maintenant: Date): ResultatEnLigne {
  const disponibilite = etat?.disponibilite;
  const connu = ["en_ligne", "contenu_anormal", "hors_ligne", "a_verifier"];
  if (!disponibilite || tropAncien(disponibilite.dernier_controle, maintenant) || !connu.includes(disponibilite.etat)) {
    return { etat: "non_collecte" };
  }
  return {
    etat: disponibilite.etat as EtatEnLigne,
    depuis: disponibilite.etat === "en_ligne" ? undefined : disponibilite.depuis ?? undefined,
    message: disponibilite.message || undefined,
  };
}

// État d'une ligne. Les mises à jour ne sont pas encore collectées : une ligne sans anomalie reste « Partiel », jamais « À jour »
export function etatDeLaLigne(sauvegarde: ResultatSauvegarde, enLigne: ResultatEnLigne): EtatLigne {
  if (["hors_ligne", "contenu_anormal"].includes(enLigne.etat) || ["echec", "echec_envoi", "aucune", "en_retard"].includes(sauvegarde.etat)) {
    return "a_traiter";
  }
  if (enLigne.etat === "a_verifier" || ["avertissement", "jamais"].includes(sauvegarde.etat)) {
    return "a_surveiller";
  }
  if (enLigne.etat === "non_collecte" && ["non_collecte", "autre", "non_prevue"].includes(sauvegarde.etat)) {
    return "non_collecte";
  }
  return "partiel";
}

export const RANG_LIGNE: Record<EtatLigne, number> = { a_traiter: 0, a_surveiller: 1, partiel: 2, a_jour: 3, non_collecte: 4 };

// Date de la collecte la plus récente, pour le bandeau de la page
export function derniereCollecte(etats: SiteEtatType[]): Date | undefined {
  return etats.reduce((d, etat) => (etat.collecte && (!d || etat.collecte > d) ? etat.collecte : d), undefined as Date | undefined);
}
